import { useCallback, useEffect, useState } from 'react';

import {
  attentionReasonAr,
  needsAttention,
  summarise,
} from '../../features/committee/application/progressSummary';
import type {
  BranchProgressCount,
  EmployeeProgressRow,
  PendingRegistrationRow,
} from '../../features/committee/application/ports/progressPort';
import {
  toCorrection,
  type CorrectionFormValues,
} from '../../features/committee/application/employeeCorrection';
import { toSearchToken } from '../../core/primitives/nameSearch';
import { EmployeeCorrectionCard } from '../../pages/EmployeeCorrectionCard';
import {
  ProgressDashboardPage,
  type DashboardFilters,
} from '../../pages/ProgressDashboardPage';

import { useServices } from '../providers/servicesContext';

/** One page at a time. See `firebaseProgressAdapter` on why (§11.5.1). */
const PAGE_SIZE = 25;

export function ProgressScreen({
  branchNames,
  branches,
  canCorrect,
}: {
  readonly branchNames: Readonly<Record<string, string>>;
  readonly branches: readonly { readonly id: string; readonly nameAr: string }[];
  /** Reading progress and correcting a record are different permissions. */
  readonly canCorrect: boolean;
}) {
  const { progress, employeeAdmin } = useServices();
  const [counts, setCounts] = useState<readonly BranchProgressCount[]>([]);
  const [rows, setRows] = useState<readonly EmployeeProgressRow[]>([]);
  const [pending, setPending] = useState<readonly PendingRegistrationRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [noticeAr, setNoticeAr] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>({
    employeeNumber: '',
    nameFragment: '',
    branchId: '',
  });
  const [editing, setEditing] = useState<{
    readonly employeeId: string;
    readonly employeeNumber: string;
    readonly values: CorrectionFormValues;
  } | null>(null);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const nameToken = toSearchToken(filters.nameFragment);
  const searching = nameToken !== null || filters.employeeNumber.trim() !== '';

  useEffect(() => {
    let cancelled = false;

    /*
     * Every filter is asked of the server now, the name included.
     *
     * It used to narrow the rows already loaded, which on 872 people meant a
     * search found nobody past the first page — "أنس" returned nothing while
     * his employee number returned him.
     */
    Promise.all([
      progress.countByBranch(),
      progress.listEmployeeProgress({
        pageSize: PAGE_SIZE,
        ...(filters.employeeNumber.trim() === ''
          ? {}
          : { employeeNumber: filters.employeeNumber.trim() }),
        ...(nameToken === null ? {} : { nameToken }),
        ...(filters.branchId === '' ? {} : { branchId: filters.branchId }),
      }),
      progress.listPendingRegistrations(),
    ])
      .then(([branchCounts, page, pendingRows]) => {
        if (cancelled) return;
        setCounts(branchCounts);
        setRows(page.rows);
        setCursor(page.nextCursor);
        setReachedEnd(page.nextCursor === null);
        setPending(pendingRows);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل لوحة المتابعة.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [progress, filters.employeeNumber, nameToken, filters.branchId, reloadToken]);

  const handleLoadMore = useCallback(() => {
    if (cursor === null) return;

    setLoading(true);
    progress
      .listEmployeeProgress({
        cursor,
        pageSize: PAGE_SIZE,
        ...(filters.branchId === '' ? {} : { branchId: filters.branchId }),
      })
      .then((page) => {
        setRows((current) => [...current, ...page.rows]);
        setCursor(page.nextCursor);
        setReachedEnd(page.nextCursor === null);
        setLoading(false);
      })
      .catch((error: unknown) => {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل المزيد.');
        setLoading(false);
      });
  }, [progress, cursor, filters.branchId]);

  const openCorrection = useCallback(
    (employeeId: string) => {
      setErrorAr(null);
      setNoticeAr(null);

      const row = rows.find((entry) => entry.employeeId === employeeId);

      void employeeAdmin
        .getEmployee(employeeId)
        .then((record) => {
          if (!record) {
            setErrorAr('لم يُعثر على سجل الموظف.');
            return;
          }

          setEditing({
            employeeId,
            employeeNumber: row?.employeeNumber ?? employeeId,
            values: {
              fullName: record.fullName,
              branchId: record.branchId ?? '',
              workEmail: record.workEmail ?? '',
              phone: record.phone ?? '',
              address: record.address ?? '',
              declaredChildrenCount: String(record.declaredChildrenCount),
              declaredMaritalStatus:
                record.declaredMaritalStatus === null
                  ? ''
                  : record.declaredMaritalStatus
                    ? 'married'
                    : 'single',
            },
          });
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر فتح السجل.');
        });
    },
    [employeeAdmin, rows],
  );

  const saveCorrection = useCallback(
    (values: CorrectionFormValues) => {
      if (!editing) return;

      setSavingCorrection(true);
      setErrorAr(null);

      void employeeAdmin
        .correctEmployee(editing.employeeId, toCorrection(values))
        .then(() => {
          setEditing(null);
          setNoticeAr('حُفظ التصحيح.');
          // Re-read rather than patch the row in place: the list is the
          // server's answer, and a locally edited copy would claim a write
          // this screen only requested.
          setReloadToken((token) => token + 1);
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر حفظ التصحيح.');
        })
        .finally(() => {
          setSavingCorrection(false);
        });
    },
    [employeeAdmin, editing],
  );

  const overall = summarise(counts);

  /*
   * Searching shows everyone it matched; browsing shows who needs chasing.
   *
   * `needsAttention` drops the people who are already done, which is right for
   * a worklist and wrong for a search: looking someone up and being told they
   * do not exist, because they finished, is worse than useless.
   */
  const visible = searching ? [...rows] : needsAttention(rows);
  const branchName = (id: string | null): string =>
    id === null ? '—' : (branchNames[id] ?? id);

  const dashboard = (
    <ProgressDashboardPage
      tiles={[
        { labelAr: 'إجمالي الموظفين', value: overall.total, tone: 'neutral' },
        { labelAr: 'لم يبدأوا', value: overall.notStarted, tone: 'warning' },
        { labelAr: 'قيد التعبئة', value: overall.inProgress, tone: 'neutral' },
        { labelAr: 'أرسلوا الإقرار', value: overall.submitted, tone: 'success' },
      ]}
      submittedPercent={overall.submittedPercent}
      attention={visible.map((row) => ({
        employeeId: row.employeeId,
        fullName: row.fullName,
        employeeNumber: row.employeeNumber,
        branchNameAr: branchName(row.branchId),
        reasonAr: attentionReasonAr(row),
        // A disputed mapping blocks a real person and mis-routes others until
        // HR corrects the row, so it is the one thing marked urgent.
        urgent: row.emailMappingDisputed,
      }))}
      pending={pending.map((row) => ({
        authUid: row.authUid,
        fullNameAr: row.fullNameAr,
        email: row.email,
        branchNameAr: branchName(row.branchId),
        reasonAr:
          row.reason === 'name_mismatch'
            ? 'الاسم لا يطابق السجل المرتبط ببريده'
            : 'لا يوجد سجل مطابق',
      }))}
      loading={loading}
      errorAr={errorAr}
      noticeAr={noticeAr}
      hasMore={!reachedEnd && filters.employeeNumber.trim() === ''}
      searching={searching}
      onLoadMore={handleLoadMore}
      branches={branches}
      filters={filters}
      onFiltersChange={(next) => {
        setNoticeAr(null);
        setFilters(next);
      }}
      onEdit={canCorrect ? openCorrection : null}
    />
  );

  return editing === null ? (
    dashboard
  ) : (
    <>
      <EmployeeCorrectionCard
        employeeNumber={editing.employeeNumber}
        initialValues={editing.values}
        branches={branches}
        saving={savingCorrection}
        errorAr={errorAr}
        onSave={saveCorrection}
        onCancel={() => {
          setEditing(null);
        }}
      />
      {dashboard}
    </>
  );
}
